/**
 * useProjectDeepLink - Hook for project deep linking
 *
 * Provides URL-driven state management for project navigation:
 * - Reads projectId, section, entityId from URL
 * - Provides navigation functions that update URL
 * - Syncs URL state with component state
 *
 * URL Structure:
 * - /projects/:projectId                    → Project dashboard
 * - /projects/:projectId/:section           → Project section (tasks, board, etc.)
 * - /projects/:projectId/:section/:entityId → Entity open in widget
 *
 * Usage:
 * ```tsx
 * function ProjectDetail() {
 *   const {
 *     projectId,
 *     section,
 *     entityId,
 *     isWidgetOpen,
 *     navigateToSection,
 *     openEntity,
 *     closeEntity,
 *     copyLink,
 *   } = useProjectDeepLink();
 *
 *   // Widget renders based on entityId from URL
 *   return (
 *     <>
 *       <SectionContent section={section} />
 *       {isWidgetOpen && renderWidgetByType(entityType, {
 *         entityId,
 *         onClose: closeEntity,
 *       })}
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPrefix, type IdPrefix } from '@business-assistant/shared/domain';

// =============================================================================
// TYPES
// =============================================================================

export type ProjectSection =
  | 'dashboard'
  | 'tasks'
  | 'board'
  | 'tickets'
  | 'milestones'
  | 'files'
  | 'times'
  | 'messages'
  | 'notebooks'
  | 'links'
  | 'comments'
  | 'people'
  | 'gantt'
  | 'timeline'
  | 'table'
  | 'finance'
  | 'risks'
  | 'proofs'
  | 'forms'
  | 'settings';

/**
 * Maps entity prefixes to their corresponding section
 */
const ENTITY_TO_SECTION: Record<IdPrefix, ProjectSection> = {
  tsk: 'tasks',
  mst: 'milestones',
  tme: 'times',
  prj: 'dashboard',
  cnt: 'people',
  inv: 'finance',
  cal: 'tasks', // calls linked to tasks
  eml: 'messages',
  msg: 'messages',
  evt: 'tasks', // events linked to tasks
  cht: 'messages',
  cnv: 'messages',
  mov: 'finance',
  doc: 'files',
  not: 'notebooks',
  cmt: 'comments',
  lnk: 'links',
  tkt: 'tickets',
  rsk: 'risks',
  prf: 'proofs',
  frm: 'forms',
  usr: 'people',
  set: 'settings',
};

export interface UseProjectDeepLinkResult {
  /** Current project ID from URL */
  projectId: string | undefined;

  /** Current section from URL (default: 'dashboard') */
  section: ProjectSection;

  /** Entity ID if widget is open (from URL) */
  entityId: string | undefined;

  /** Entity type prefix (e.g., 'tsk', 'mst') */
  entityType: IdPrefix | undefined;

  /** Whether a widget is currently open */
  isWidgetOpen: boolean;

  /** Whether project edit mode is active (/projects/:id/edit) */
  isEditMode: boolean;

  /** Navigate to a project section */
  navigateToSection: (section: ProjectSection) => void;

  /** Open an entity in widget (updates URL) */
  openEntity: (entityId: string, section?: ProjectSection) => void;

  /** Close current widget (removes entityId from URL) */
  closeEntity: () => void;

  /** Open project edit form */
  openEditMode: () => void;

  /** Close project edit form */
  closeEditMode: () => void;

  /** Copy current URL to clipboard */
  copyLink: () => Promise<void>;

  /** Get current full URL */
  getCurrentUrl: () => string;

  /** Navigate back to project list */
  goToProjectList: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook parameters - pass projectId from props for reliability
 */
interface UseProjectDeepLinkParams {
  /** Project ID from parent component props (optional, will parse from URL if not provided) */
  projectIdFromProps?: string;
}

export function useProjectDeepLink(params?: UseProjectDeepLinkParams): UseProjectDeepLinkResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL manually since routes render null and AppNew decides what to show
  // URL patterns:
  // - /projects/:projectId                    → Project dashboard
  // - /projects/:projectId/edit               → Project edit form
  // - /projects/:projectId/:section           → Project section (tasks, board, etc.)
  // - /projects/:projectId/:section/:entityId → Entity open in widget
  const urlParts = useMemo(() => {
    // Match both /projects/ and /projects/ for compatibility
    const match = location.pathname.match(/\/(?:progetti|projects)\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?/);

    if (!match) return { projectId: undefined, section: 'dashboard' as ProjectSection, entityId: undefined, isEditMode: false };

    const projectId = match[1];
    const secondSegment = match[2];
    const thirdSegment = match[3];

    // URL patterns:
    // - /projects/:projectId                    → Edit form (isEditMode = true)
    // - /projects/:projectId/dashboard          → Project dashboard
    // - /projects/:projectId/:section           → Project section (tasks, board, etc.)
    // - /projects/:projectId/:section/:entityId → Entity open in widget

    // No section = edit mode
    const isEditMode = !secondSegment;

    return {
      projectId,
      section: (secondSegment as ProjectSection) || 'dashboard',
      entityId: thirdSegment,
      isEditMode,
    };
  }, [location.pathname]);

  // Prefer projectId from props if provided (more reliable)
  const projectId = params?.projectIdFromProps || urlParts.projectId;
  const { section, entityId, isEditMode } = urlParts;

  // Determine entity type from ID prefix
  const entityType = useMemo(() => {
    if (!entityId) return undefined;
    return getPrefix(entityId) as IdPrefix | undefined;
  }, [entityId]);

  const isWidgetOpen = !!entityId;

  // === NAVIGATION FUNCTIONS ===

  const navigateToSection = useCallback(
    (newSection: ProjectSection) => {
      if (!projectId) return;
      // Always include section in URL (dashboard is explicit)
      navigate(`/projects/${projectId}/${newSection}`);
    },
    [projectId, navigate]
  );

  const openEntity = useCallback(
    (newEntityId: string, targetSection?: ProjectSection) => {
      if (!projectId) {
        return;
      }

      // Determine section from entity type if not provided
      let sectionToUse = targetSection || section;
      if (!targetSection) {
        const prefix = getPrefix(newEntityId) as IdPrefix;
        if (prefix && ENTITY_TO_SECTION[prefix]) {
          sectionToUse = ENTITY_TO_SECTION[prefix];
        }
      }

      navigate(`/projects/${projectId}/${sectionToUse}/${newEntityId}`);
    },
    [projectId, section, navigate]
  );

  const closeEntity = useCallback(() => {
    if (!projectId) return;
    // Always include section in URL
    navigate(`/projects/${projectId}/${section}`);
  }, [projectId, section, navigate]);

  const openEditMode = useCallback(() => {
    if (!projectId) return;
    // No section = edit mode
    navigate(`/projects/${projectId}`);
  }, [projectId, navigate]);

  const closeEditMode = useCallback(() => {
    if (!projectId) return;
    // Go to dashboard after closing edit
    navigate(`/projects/${projectId}/dashboard`);
  }, [projectId, navigate]);

  const copyLink = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, []);

  const getCurrentUrl = useCallback(() => {
    return window.location.href;
  }, []);

  const goToProjectList = useCallback(() => {
    navigate('/projects');
  }, [navigate]);

  return {
    projectId,
    section,
    entityId,
    entityType,
    isWidgetOpen,
    isEditMode,
    navigateToSection,
    openEntity,
    closeEntity,
    openEditMode,
    closeEditMode,
    copyLink,
    getCurrentUrl,
    goToProjectList,
  };
}

/**
 * Helper to build project URLs without navigation
 */
export function buildProjectUrl(
  projectId: string,
  section?: ProjectSection,
  entityId?: string
): string {
  const base = `/projects/${projectId}`;
  if (!section || section === 'dashboard') {
    return entityId ? `${base}/dashboard/${entityId}` : base;
  }
  return entityId ? `${base}/${section}/${entityId}` : `${base}/${section}`;
}
